import React from 'react'

export function SettingsTitle({
  title,
  subtitle,
  right,
}: {
  title: string
  subtitle?: string
  right?: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-extrabold">{title}</h1>
        {subtitle ? <div className="mt-2 text-sm text-white/70">{subtitle}</div> : null}
      </div>
      {right}
    </div>
  )
}

export function SettingsCard({
  title,
  subtitle,
  right,
  children,
  accent = 'default',
}: {
  title?: string
  subtitle?: string
  right?: React.ReactNode
  children: React.ReactNode
  accent?: 'default' | 'key'
}) {
  const ring = accent === 'key' ? 'ring-1 ring-white/10' : ''
  return (
    <div
      className={[
        'rounded-xl border border-zinc-800',
        ring,
        'bg-zinc-950/40',
        'p-4',
      ].join(' ')}
    >
      {title || subtitle || right ? (
        <div className="flex items-start justify-between gap-4">
          <div>
            {title ? <div className="text-lg font-extrabold">{title}</div> : null}
            {subtitle ? <div className="text-sm text-white/70 mt-0.5">{subtitle}</div> : null}
          </div>
          {right}
        </div>
      ) : null}
      <div className={title || subtitle || right ? 'mt-4' : ''}>{children}</div>
    </div>
  )
}

export function SettingsChip({
  children,
  variant = 'neutral',
}: {
  children: React.ReactNode
  variant?: 'neutral' | 'available' | 'vesting' | 'pro'
}) {
  const cls =
    variant === 'available'
      ? 'bg-emerald-500/10 text-emerald-200 border-emerald-500/20'
      : variant === 'vesting'
        ? 'bg-sky-500/10 text-sky-200 border-sky-500/20'
        : variant === 'pro'
          ? 'bg-amber-500/10 text-amber-200 border-amber-500/20'
        : 'bg-white/5 text-white/80 border-white/10'
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[12px] ${cls}`}>{children}</span>
}

export function SettingsButton({
  children,
  onClick,
  variant = 'primary',
  disabled = false,
  className = '',
  ...rest
}: {
  children: React.ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  disabled?: boolean
  className?: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const base = 'inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border'
  const styles: Record<typeof variant, string> = {
    primary: 'bg-sky-600 text-white border-sky-500/40 hover:bg-sky-500 hover:border-sky-400/50',
    secondary: 'bg-sky-600/15 text-sky-100 border-sky-500/30 hover:bg-sky-600/25 hover:border-sky-400/40',
    ghost: 'bg-transparent text-white/85 border-transparent hover:bg-white/10 hover:border-white/10',
    danger: 'bg-red-500/15 text-red-200 border-red-500/30 hover:bg-red-500/25 hover:border-red-500/40',
  }

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      {...rest}
      className={[
        base,
        styles[variant],
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
        className,
      ].join(' ')}
    >
      {children}
    </button>
  )
}
